'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date()?.getFullYear()?.toString());
  }, []);

  const footerLinks = {
    platform: [
      { label: 'Browse Courses', href: '/course-catalog' },
      { label: 'Live Classes', href: '/live-classroom' },
      { label: 'Student Dashboard', href: '/student-dashboard' },
      { label: 'Become Instructor', href: '/course-catalog' }
    ],
    company: [
      { label: 'About Us', href: '/homepage' },
      { label: 'Careers', href: '/homepage' },
      { label: 'Press Kit', href: '/homepage' },
      { label: 'Contact', href: '/homepage' }
    ],
    resources: [
      { label: 'Help Center', href: '/homepage' },
      { label: 'Community Forum', href: '/homepage' },
      { label: 'Blog', href: '/homepage' },
      { label: 'Success Stories', href: '/homepage' }
    ],
    legal: [
      { label: 'Privacy Policy', href: '/homepage' },
      { label: 'Terms of Service', href: '/homepage' },
      { label: 'Cookie Policy', href: '/homepage' },
      { label: 'Refund Policy', href: '/homepage' }
    ]
  };

  const socialLinks = [
    { icon: 'facebook', label: 'Facebook', href: '#' },
    { icon: 'twitter', label: 'Twitter', href: '#' },
    { icon: 'linkedin', label: 'LinkedIn', href: '#' },
    { icon: 'instagram', label: 'Instagram', href: '#' }
  ];

  return (
    <footer className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-2 mb-4">
              <AppImage 
                src="/assets/images/WeSkill_Logo-1768646176551.png"
                alt="WeSkill Academy Logo"
                width={120}
                height={40}
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-white/70 mb-4 leading-relaxed">
              Empowering professionals through expert-led, live learning experiences. Transform your career with flexible, high-quality education.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.icon}
                  href={social?.href}
                  aria-label={social?.label}
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-300"
                >
                  <Icon name="ShareIcon" size={20} variant="outline" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-headline text-lg font-bold mb-4">Platform</h3>
            <ul className="space-y-2">
              {footerLinks?.platform?.map((link) => (
                <li key={link?.label}>
                  <Link
                    href={link?.href}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-headline text-lg font-bold mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks?.company?.map((link) => (
                <li key={link?.label}>
                  <Link
                    href={link?.href}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-headline text-lg font-bold mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks?.resources?.map((link) => (
                <li key={link?.label}>
                  <Link
                    href={link?.href}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-headline text-lg font-bold mb-4">Legal</h3>
            <ul className="space-y-2">
              {footerLinks?.legal?.map((link) => (
                <li key={link?.label}>
                  <Link
                    href={link?.href}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-white/70 text-sm">
              {isHydrated ? `© ${currentYear}` : '© 2026'} WeSkill Academy. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-white/70 text-sm">
                <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-success" />
                <span>SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2 text-white/70 text-sm">
                <Icon name="CheckBadgeIcon" size={16} variant="solid" className="text-primary" />
                <span>Accredited</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;